import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Instagram, Youtube, Facebook, Linkedin, MapPinned } from "lucide-react";
import { business, fullAddress, navLinks, routes, whatsappLink } from "@/lib/site";

const social = [
  { icon: Instagram, href: business.socials.instagram, label: "Instagram" },
  { icon: Youtube, href: business.socials.youtube, label: "YouTube" },
  { icon: Facebook, href: business.socials.facebook, label: "Facebook" },
  { icon: Linkedin, href: business.socials.linkedin, label: "LinkedIn" },
  { icon: MapPinned, href: business.socials.google, label: "Google Business" },
];

export const Footer = () => {
  return (
    <footer className="bg-navy text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-28 xl:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-orange text-white font-heading font-extrabold text-lg">
                S
              </span>
              <div className="leading-tight">
                <span className="block font-heading font-bold text-lg">{business.doctorName}</span>
                <span className="block text-xs text-white/60">{business.clinicName}</span>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              Personalized chiropractic and pain care in Varanasi — helping you move better,
              manage pain and improve mobility through a non-surgical approach.
            </p>
            <p className="font-heading font-semibold text-brand-orange">"{business.tagline}"</p>
            <div className="flex items-center gap-3 mt-5">
              {social.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase().replace(/\s/g, "")}`}
                  className="grid h-9 w-9 place-items-center rounded-full bg-white/10 hover:bg-brand-orange transition-colors"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-base mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/70 hover:text-brand-orange transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-base mb-4">Resources</h4>
            <ul className="space-y-2.5">
              <li><Link to={routes.resources} className="text-sm text-white/70 hover:text-brand-orange transition-colors">Blogs</Link></li>
              <li><Link to={routes.resources} className="text-sm text-white/70 hover:text-brand-orange transition-colors">Videos</Link></li>
              <li><Link to={routes.resources} className="text-sm text-white/70 hover:text-brand-orange transition-colors">Gallery</Link></li>
              <li><Link to={routes.privacy} className="text-sm text-white/70 hover:text-brand-orange transition-colors">Privacy Policy</Link></li>
              <li><Link to={routes.terms} className="text-sm text-white/70 hover:text-brand-orange transition-colors">Terms &amp; Conditions</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-base mb-4">Contact</h4>
            <ul className="space-y-3.5 text-sm text-white/70">
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-brand-orange" />
                <span>{fullAddress}</span>
              </li>
              <li>
                <a href={`tel:${business.phoneTel}`} className="flex gap-3 hover:text-brand-orange transition-colors" data-testid="footer-phone">
                  <Phone className="h-5 w-5 shrink-0 text-brand-orange" />
                  <span>{business.phoneDisplay}</span>
                </a>
              </li>
              <li className="flex gap-3">
                <Clock className="h-5 w-5 shrink-0 text-brand-orange" />
                <span>
                  {business.timings.map((t) => (
                    <span key={t.days} className="block">{t.days}: {t.hours}</span>
                  ))}
                </span>
              </li>
            </ul>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-orange-dark transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} {business.clinicName}. All rights reserved.</p>
          <p>Chiropractor in Varanasi · Pain &amp; Spine Care</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
